import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import { setToken } from './auth'

function fromBase64Url(value: string): string {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4)
  return decodeURIComponent(atob(padded))
}

async function fetchToken(): Promise<string | null> {
  // Whichever page this is (popup or the fallback top-level tab), it's
  // running as a genuine top-level context right now — the oauth2-proxy
  // cookie set moments ago at /oauth2/callback works normally here.
  try {
    const res = await fetch('/api/auth/token', { credentials: 'include' })
    if (res.ok) return (await res.json()).token ?? null
  } catch {
    /* falls through to null below */
  }
  return null
}

// A path segment after /login-complete means this is the popup-blocked
// fallback (see App.tsx's signIn) carrying a base64url-encoded return URL —
// this IS the main tab, having navigated away and back. No segment means
// this is the popup itself.
const match = window.location.pathname.match(/^\/login-complete(?:\/(.+))?$/)

if (match) {
  const returnTo = match[1] ? fromBase64Url(match[1]) : null
  fetchToken().then((token) => {
    if (returnTo) {
      if (token) setToken(token)
      window.location.href = returnTo
      return
    }

    // Popup flow: relay the token back via postMessage using event.source,
    // not window.opener — Microsoft's login page severs that reference via
    // Cross-Origin-Opener-Policy partway through this popup's navigation
    // history, so it can't be relied on here. event.source on a *received*
    // message still correctly points back to whoever sent it regardless.
    function respond(event: MessageEvent) {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'devops-api:request-token') return
      ;(event.source as WindowProxy | null)?.postMessage(
        { type: 'devops-api:token', token },
        window.location.origin,
      )
    }
    window.addEventListener('message', respond)

    // Don't linger forever if the opener never pings (e.g. it gave up already).
    setTimeout(() => window.close(), 8000)
  })
} else {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  )
}
